import { cmp, div, quantise, sum, ZERO, type Money } from '@/lib/money'
import type { PriceHistoryPoint } from '@/schemas/prices'
import type { TradingPeriod } from '@/features/trading-history/lib/tradingSearch'

/**
 * The sessions a period covers, oldest first as served. A preset is the series
 * mm-be already cut to it; a custom window keeps the sessions dated inside it
 * (ISO days compare as strings).
 */
export const sessionsIn = (
  points: readonly PriceHistoryPoint[],
  period: TradingPeriod,
): readonly PriceHistoryPoint[] =>
  period.kind === 'preset'
    ? points
    : points.filter((point) => point.date >= period.from && point.date <= period.to)

/** A figure the record holds: present and above zero. NEPSE has no price or turnover of 0. */
const recorded = (value: Money | null): value is Money => value !== null && cmp(value, ZERO) > 0

/**
 * The price a session averaged: its turnover over its shares. Not a field
 * mm-be serves, so it is left out (null) when either figure is missing rather
 * than guessed — a missing turnover is not a session that traded nothing.
 */
export const sessionAveragePrice = (point: PriceHistoryPoint): Money | null =>
  recorded(point.turnover) && point.volume !== null && point.volume > 0
    ? quantise(div(point.turnover, point.volume))
    : null

export interface TradingSummary {
  /** Sessions in the period. */
  days: number
  /** Shares traded across them; null when no session reports a volume. */
  totalShares: number | null
  /** Rupees traded across them; null when no session reports a turnover. */
  totalTurnover: Money | null
  /** Total turnover over total shares, on the sessions that report both. */
  weightedAveragePrice: Money | null
  averageDailyShares: number | null
  averageDailyTurnover: Money | null
  /** The newest session's close and move, for the Stock Trading head. */
  lastClose: Money | null
  lastChange: Money | null
}

/**
 * The strip above either table. Every average leaves a session without the
 * figure out of both its sum and its count, as mm-be's own range summary does,
 * so one gap cannot read as a quiet day.
 */
export const summarise = (sessions: readonly PriceHistoryPoint[]): TradingSummary => {
  const withShares = sessions.filter((s) => s.volume !== null)
  const withTurnover = sessions.filter((s) => recorded(s.turnover))
  const withBoth = sessions.filter((s) => recorded(s.turnover) && s.volume !== null && s.volume > 0)

  const shares = (rows: readonly PriceHistoryPoint[]) =>
    rows.reduce((total, s) => total + (s.volume ?? 0), 0)
  const turnover = (rows: readonly PriceHistoryPoint[]) =>
    sum(rows.flatMap((s) => (recorded(s.turnover) ? [s.turnover] : [])))

  const totalShares = withShares.length === 0 ? null : shares(withShares)
  const totalTurnover = withTurnover.length === 0 ? null : turnover(withTurnover)
  const bothShares = shares(withBoth)
  const newest = sessions.at(-1)

  return {
    days: sessions.length,
    totalShares,
    totalTurnover,
    weightedAveragePrice: bothShares > 0 ? quantise(div(turnover(withBoth), bothShares)) : null,
    averageDailyShares: totalShares === null ? null : Math.round(totalShares / withShares.length),
    averageDailyTurnover:
      totalTurnover === null ? null : quantise(div(totalTurnover, withTurnover.length)),
    lastClose: newest?.close ?? null,
    lastChange: newest?.percentChange ?? null,
  }
}

/** One CSV cell: quoted when it holds a comma, a quote or a line break. */
const csvCell = (cell: string): string =>
  /[",\n\r]/.test(cell) ? `"${cell.replaceAll('"', '""')}"` : cell

export const toCsv = (header: readonly string[], rows: readonly (readonly string[])[]): string =>
  [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n')

/**
 * Hands `text` to the browser as a file. A blob URL on a throwaway link, not a
 * server round trip: the rows are already on the page.
 */
export const downloadCsv = (filename: string, text: string): void => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
